/**
 * ScriptLoader is a utility class for dynamically loading external JavaScript files.
 * It ensures that each script is only loaded once into the document.
 */
export class ScriptLoader {
    /**
     * A set of loaded script URLs to prevent duplicate script loading.
     */
    private static loadedScripts: Set<string> = new Set();

    /**
     * A set of loaded stylesheet URLs to prevent duplicate stylesheet loading.
     */
    private static loadedStylesheets: Set<string> = new Set();

    /**
     * Loads a JavaScript file dynamically if it has not already been loaded.
     * 
     * @param url The URL of the external JavaScript file to load.
     * @returns A Promise that resolves when the script is loaded successfully, or rejects if the script fails to load.
     */
    static LoadScript(url: string): Promise<void> {
        return new Promise((resolve, reject) => {
            // Check if the script is already loaded
            if (this.loadedScripts.has(url)) {
                resolve();
                return;
            }

            // Create script element
            const script = document.createElement('script');
            script.src = url;
            script.async = true; // Load script asynchronously

            // Success handler: Add script to the set and resolve the Promise
            script.onload = () => {
                this.loadedScripts.add(url);
                resolve();
            };

            // Error handler: Reject the Promise with an error message
            script.onerror = () => {
                reject(new Error(`ScriptLoader: Failed to load script ${url}`));
            };

            // Append script to the document head
            document.head.appendChild(script);
        });
    }

    /**
     * Loads a CSS stylesheet dynamically if it has not already been loaded.
     * 
     * This method creates a <link> element to load the specified stylesheet and appends it to the document's <head>.
     * If the stylesheet is already loaded (based on the URL), it does not attempt to load it again.
     * 
     * @param url The URL of the external CSS stylesheet to load.
     * @returns A Promise that resolves when the stylesheet is loaded successfully, or rejects if the stylesheet fails to load.
     */
    static LoadStylesheet(url: string): Promise<void> {
        return new Promise((resolve, reject) => {
            // Check if the stylesheet is already loaded
            if (this.loadedStylesheets.has(url)) {
                resolve();
                return;
            }

            // Create link element for stylesheet
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = url;

            // Success handler: Add stylesheet to the set and resolve the Promise
            link.onload = () => {
                this.loadedStylesheets.add(url);
                resolve();
            };

            // Error handler: Reject the Promise with an error message
            link.onerror = () => {
                reject(new Error(`ScriptLoader: Failed to load stylesheet ${url}`));
            };

            // Append link to the document head
            document.head.appendChild(link);
        });
    }
}

window['LoadScript'] = function (url: string) {
    return ScriptLoader.LoadScript(url);
}

window['LoadStylesheet'] = function (url: string) {
    return ScriptLoader.LoadStylesheet(url);
}